import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  PButton,
  PButtonPure,
  PText,
} from '@porsche-design-system/components-react';
import { useEffect, useState } from 'react';

import { ActionGroup } from './ActionGroup';
import { FileType } from './FileType';
import { Modal } from './Modal';

const ItemWrapper = styled('div', {
  base: {
    display: 'inline-flex',
    maxWidth: 250,
    marginBottom: 'sm',
  },

  variants: {
    download: {
      true: {
        cursor: 'pointer',
      },
    },
  },
});

const InfoWrapper = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    whiteSpace: 'nowrap',
    columnGap: 8,
  },
});

const LabelWrapper = styled('div', {
  base: {
    gridColumn: 'span 2',
    display: 'grid',
    gridTemplateColumns: 'calc(100% - 40px) auto',
  },
});

const MetaText = styled(
  PText,
  { base: { overflow: 'hidden' } },
  { forwardProps: ['color'] },
);

export const AttachmentList = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  return <div>{children}</div>;
};

export const AttachmentListItem = ({
  children,
  fileType,
  onClick,
  canPreview,
  previewHeading,
  onPreview,
  i18n: { cancel, download },
  isLoading,
}: {
  children: React.ReactNode;
  fileType: string;
  onClick?: () => void;
  canPreview?: boolean;
  previewHeading?: string;
  onPreview?: () => Promise<{
    url: string;
  }>;
  i18n: {
    cancel: string;
    download: string;
  };
  isLoading?: boolean;
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [previewError, setPreviewError] = useState<string>();

  useEffect(() => {
    if (showPreview) {
      let isActive = true;

      setPreviewUrl(undefined);
      setPreviewError(undefined);
      onPreview?.()
        .then((result) => {
          if (!isActive) {
            return;
          }

          setPreviewUrl(result.url);
        })
        .catch((error: unknown) => {
          if (!isActive) {
            return;
          }

          const rawMessage =
            (error as { response?: { errors?: { message?: string }[] } })
              ?.response?.errors?.[0]?.message ??
            (error instanceof Error
              ? error.message
              : typeof error === 'string'
                ? error
                : undefined);

          setPreviewError(rawMessage);
        });

      return () => {
        isActive = false;
      };
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview]);

  return (
    <>
      <ItemWrapper
        onClick={canPreview ? () => setShowPreview(true) : onClick}
        download={!!onClick || canPreview}
      >
        <FileType loading={isLoading} value={fileType} size="lg" />
        <InfoWrapper>{children}</InfoWrapper>
      </ItemWrapper>
      <Modal
        open={showPreview}
        size="large"
        onDismiss={() => {
          setShowPreview(false);
          setPreviewError(undefined);
        }}
      >
        <Modal.Header>{previewHeading}</Modal.Header>

        {previewError ? (
          <PText>{previewError}</PText>
        ) : previewUrl ? (
          <img width="100%" src={previewUrl} />
        ) : null}

        <Modal.Footer sticky>
          <PButton
            type="button"
            variant="secondary"
            onClick={() => setShowPreview(false)}
          >
            {cancel}
          </PButton>
          {previewUrl && !previewError ? (
            <PButton type="button" onClick={onClick} loading={isLoading}>
              {download}
            </PButton>
          ) : null}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export const AttachmentListItemLabel = ({
  children,
  actions,
  onDelete,
  i18n: { deleteHeading, yes, cancel } = {},
}: {
  children: string;
  actions?: React.ReactNode;
  onDelete?: () => void;
  i18n?: {
    deleteHeading?: string;
    yes?: string;
    cancel?: string;
  };
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (process.env.NODE_ENV !== 'production') {
    if (onDelete && !(deleteHeading && yes && cancel)) {
      console.warn(
        'You have to provide i18n props when using onDelete prop on AttachmentListItemLabel',
      );
    }
  }

  return (
    <>
      <LabelWrapper>
        <PText ellipsis title={children}>
          {children}
        </PText>
        <div>
          {onDelete && (
            <PButtonPure
              data-testid="attachment-delete-button"
              icon="delete"
              type="button"
              hideLabel
              onClick={(evt) => {
                evt.stopPropagation();
                setShowDeleteModal(true);
              }}
            />
          )}
          {actions}
        </div>
      </LabelWrapper>

      {onDelete && (
        <Modal
          open={showDeleteModal}
          size="small"
          onClick={(evt) => {
            // Avoid downloading the attachment when clicking on any place other than the buttons
            evt.stopPropagation();
          }}
          onDismiss={(evt) => {
            evt.stopPropagation();
            setShowDeleteModal(false);
          }}
        >
          <Modal.Header>{deleteHeading}</Modal.Header>

          <ActionGroup spacing="none">
            <PButton
              type="button"
              variant="secondary"
              onClick={(evt) => {
                evt.stopPropagation();
                setShowDeleteModal(false);
              }}
            >
              {cancel}
            </PButton>
            <PButton
              type="button"
              variant="primary"
              onClick={(evt) => {
                evt.stopPropagation();
                onDelete();
              }}
            >
              {yes}
            </PButton>
          </ActionGroup>
        </Modal>
      )}
    </>
  );
};

export const AttachmentListItemMeta = ({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) => {
  return (
    <>
      <MetaText size="xs" color="contrast-medium" ellipsis>
        {label}:
      </MetaText>
      <MetaText size="xs" color="contrast-medium" ellipsis>
        {value}
      </MetaText>
    </>
  );
};

AttachmentList.Item = AttachmentListItem;
AttachmentListItem.Label = AttachmentListItemLabel;
AttachmentListItem.Meta = AttachmentListItemMeta;
